import { useVerify } from '@entities/Authentication/model.ts';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowShortLeft } from '@shared/assets/index.ts';
import { useMobile } from '@shared/hooks/index.ts';
import { CommonButton, MediumHeader } from '@shared/ui/index.ts';
import { usePhoneVerificationIncomingModal } from '@shared/ui/modals/phone-verification-incoming-modal/helpers.ts';
import { Loader } from '@src/library/components/common/index.ts';
import ModalMiddle from '@src/library/components/common/modals/ModalMiddle/index.ts';
import { AuthDiv } from '@src/modules/Authentication/AuthFrom/AuthDiv.tsx';
import Timer from '@src/modules/Authentication/Timer/ui.tsx';
import st from './styles.module.scss';

export const PhoneVerificationIncomingModal = () => {
  const [step, setStep] = useState(1);
  const [intervalKey, setIntervalKey] = useState<NodeJS.Timeout | null>(null);
  const { isMobile } = useMobile();
  const { state, closePhoneVerificationIncomingModal } = usePhoneVerificationIncomingModal();

  const { onCheckPhoneVerification } = useVerify();
  useEffect(() => {
    if (step === 2 && !intervalKey) {
      const key = setInterval(
        () =>
          onCheckPhoneVerification({
            phonenumber: state?.phonenumber,
            verification_type: state?.verification_type || 'registration',
            type: 'INCOMING_SMS',
            onSuccess: () => {
              setStep(3);
            },
          }),
        3000
      );
      setIntervalKey(key);
    } else if (step !== 2 && intervalKey) {
      clearInterval(intervalKey);
      setIntervalKey(null);
    }
  }, [intervalKey, setIntervalKey, step, onCheckPhoneVerification, state]);

  const closeForm = useCallback(
    ({ isSuccess }: { isSuccess: boolean }) => {
      setStep(1);
      closePhoneVerificationIncomingModal({ isSuccess });
    },
    [setStep, closePhoneVerificationIncomingModal]
  );

  const title = useMemo(() => {
    let output = 'Проверка';
    if (step === 1) {
      output = 'Подтверждение номера телефона';
      if (isMobile) {
        output = 'Подтверждение номера';
      }
    }
    return output;
  }, [isMobile, step]);

  const Body = useMemo(() => {
    if (step === 1) {
      return (
        <>
          <section className={st.textWrapper}>
            <p className={st.mainText}>
              С указанного вами номера отправьте SMS-сообщение на номер <b>{state?.number}</b> с
              текстом:{' '}
            </p>
            <p className={st.code}>{state?.passcode?.split('').map((el) => <span>{el}</span>)}</p>
            <p className={st.subText}>
              После отправки сообщения нажмите на кнопку
              <br />
              “Я отправил”
            </p>
            <p className={st.info}>
              Оператор связи может взимать плату за сообщения согласно вашему тарифу. С нашей
              стороны применяются стандартные тарифы на доставку SMS.
            </p>
          </section>
          <CommonButton tabIndex={0} onClick={() => setStep(2)}>
            Я отправил
          </CommonButton>
        </>
      );
    }
    if (step === 2) {
      return (
        <section className={st.loaderSectionWrapper}>
          <Loader className={st.loader} />
          <p className={st.mainText}>Не закрывайте данную страницу, мы проверяем SMS-сообщение</p>
          <Timer className={st.timer} timeout={59000} callback={() => setStep(4)} />
        </section>
      );
    }
    if (step === 3) {
      return (
        <>
          <section className={cn(st.textWrapper, st.thirdStage)}>
            <p className={st.mainText}>Поздравляем! Номер успешно подтверждён</p>
          </section>
          <CommonButton tabIndex={0} onClick={() => closeForm({ isSuccess: true })}>
            Далее
          </CommonButton>
        </>
      );
    }
    if (step === 4) {
      return (
        <>
          <section className={cn(st.textWrapper, st.thirdStage)}>
            <p className={st.mainText}>
              {' '}
              Что-то пошло не так, нам не удалось подтвердить ваш номер телефона. Чтобы продолжить,
              повторите попытку.
            </p>
          </section>
          <CommonButton tabIndex={0} onClick={() => setStep(1)}>
            Повторить попытку
          </CommonButton>
        </>
      );
    }
  }, [state, setStep, step, closeForm]);

  return (
    <ModalMiddle
      isOpen={state?.isOpen}
      overlayClassName={state?.disableOverlayCss ? st.modalOverlay : undefined}
    >
      <MediumHeader
        onClose={() => closeForm({ isSuccess: false })}
        title={title}
        IconNode={<ArrowShortLeft />}
      />
      <AuthDiv className={st.modalWrapper}>{Body}</AuthDiv>
    </ModalMiddle>
  );
};
